import type { Board } from "../board";
import { CameraFlash } from "../effects/cameraFlash";
import { Event } from "../events/event";
import { EventType } from "../events/eventType";
import { PRIORITY_LAST, type ProcessableThing, type SomethingWithState } from "../interfaces";
import { PhysicalThing } from "../physicalThing";
import { deserializeXY } from "../serialization";
import { XY } from "../xy";
import { ZOrder } from "../zOrder";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";

export enum EquationState {
	FOUND = "FOUND",
	NOT_FOUND = "NOT_FOUND",
}

export class Equation extends PhysicalThing implements ProcessableThing, SomethingWithState {
	static key = "Equation";
	private state: EquationState = EquationState.NOT_FOUND;
	private notifiedIsHere: boolean = false;
	private notifiedEquationRead: boolean = false;
	static readonly DISTANCE_TO_READ = 3;

	constructor(xy: XY) {
		super(xy, false);
	}

	getZOrder(): number {
		return ZOrder.SCENARIO;
	}

	processTurn(board: Board): void {
		if (this.state === EquationState.FOUND) return;
		if (!board.player) return;

		if (!this.notifiedIsHere) {
			board.addEvent(new Event(EventType.EQUATION_IS_HERE));
		}
		this.notifiedIsHere = true;

		const dist = this.xy.distanceTo(board.player.xy);
		if (dist > Equation.DISTANCE_TO_READ) return;

		// Line of sight check
		if (!this.hasLineOfSight(board, board.player.xy)) return;

		this.equationFoundEffect(board);
	}

	private hasLineOfSight(board: Board, targetXY: XY): boolean {
		const dx = targetXY.x - this.xy.x;
		const dy = targetXY.y - this.xy.y;
		const steps = Math.max(Math.abs(dx), Math.abs(dy));
		if (steps === 0) return true;

		for (let i = 1; i < steps; i++) {
			const x = this.xy.x + Math.round((dx * i) / steps);
			const y = this.xy.y + Math.round((dy * i) / steps);
			const checkXY = new XY(x, y);
			if (!board.isInBounds(checkXY)) return false;
			if (!board.isPassable(checkXY)) return false;
		}
		return true;
	}

	private equationFoundEffect(board: Board): void {
		this.state = EquationState.FOUND;
		board.gameFlags.equationFound = true;
		board.gameFlags.gameWon = true;
		board.addNonPhysicalThing(new Sound(SoundId.CAMERA, this.xy));
		board.addEvent(new Event(EventType.EQUATION_FOUND));
		board.getAllXYs().forEach((p) => board.addEffect(new CameraFlash(p)));
	}

	getProcessPriority(): number {
		return PRIORITY_LAST;
	}

	getState(): EquationState {
		return this.state;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			state: this.state,
			notifiedIsHere: this.notifiedIsHere,
			notifiedEquationRead: this.notifiedEquationRead,
		};
	}

	static deserialize(data: any): Equation {
		const e = new Equation(deserializeXY(data.xy));
		e.visualId = data.visualId;
		e.state = data.state ?? EquationState.NOT_FOUND;
		e.notifiedIsHere = data.notifiedIsHere ?? false;
		e.notifiedEquationRead = data.notifiedEquationRead ?? false;
		return e;
	}
}
